import React, { useEffect, useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import classNames from 'classnames'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { nanoid } from 'nanoid'

import BurgerIcon from './burger-icon'
import Button, { DeleteButton } from './button'
import ButtonTabs from './button-tabs'
import HeaderPanel, { Panel } from './header-panel'
import Input, { Label } from './input'
import Modal from './modal'
import { LabelSlot } from './row'
import Table from './table'
import Tooltip from './tooltip'
import {
  listSavedLostLinksReports,
  removeSavedLostLinkReport,
  updateSavedLostLinkReportPrefs,
} from '../api/apollo/graphql/report-client'
import EditIcon from '../assets/edit.svg'
import { availableToShareOptions, messages } from '../core/variables'
import { getItemByKeyValue } from '../helpers'
import useLogAction from '../hooks/useLogAction'
import styles from '../styles/lost-links-manage-reports.module.scss'
import { ListSavedLostLinksReportsQuery } from '../__gql-types__/graphql'

interface ManageSavedReportsModalProps {
  toggleActive: React.Dispatch<React.SetStateAction<boolean>>
  savedReports: ListSavedLostLinksReportsQuery['report']['listSavedLinkAuditReports']['savedReports']
  reportsOrder: string[]
  setReportToEdit: React.Dispatch<React.SetStateAction<SavedReport | null>>
  setShowSaveReportModal: React.Dispatch<React.SetStateAction<boolean>>
  loading: boolean
}

export function ManageSavedReportsModal({
  toggleActive,
  savedReports,
  reportsOrder,
  setReportToEdit,
  setShowSaveReportModal,
  loading = false,
}: ManageSavedReportsModalProps) {
  const logAction = useLogAction()

  const [updateReportsOrder, { loading: updatingOrder }] = useMutation(
    updateSavedLostLinkReportPrefs,
    {
      refetchQueries: [listSavedLostLinksReports],
    },
  )
  const [deleteSavedLostLinkReport, { loading: deletingReport }] = useMutation(
    removeSavedLostLinkReport,
    {
      refetchQueries: [listSavedLostLinksReports],
    },
  )

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

  return (
    <>
      <Modal
        setIsOpen={toggleActive}
        width="wide"
        modalHeader="Manage reports"
        loading={loading || updatingOrder}
      >
        {savedReports && savedReports.length > 0 && (
          <Table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.handleColumn}>Order</th>
                <th className={styles.reportNameColumn}>Report name</th>
                <th className={styles.visibilityColumn}>Who can see this</th>
                <th className={styles.actionsColumn}>Actions</th>
              </tr>
            </thead>
            <DragDropContext
              onDragEnd={async (result) => {
                const { destination, source, draggableId } = result

                if (!destination) return

                if (
                  destination.droppableId === source.droppableId &&
                  destination.index === source.index
                ) {
                  return
                }

                const newReportsOrder = Array.from(reportsOrder)

                newReportsOrder.splice(source.index, 1)

                newReportsOrder.splice(destination.index, 0, draggableId)

                await updateReportsOrder({
                  variables: {
                    newOrder: newReportsOrder,
                  },
                })

                logAction({
                  variables: {
                    action: 'saved-lost-link-reports-update-order',
                    extra: JSON.stringify({
                      oldOrder: reportsOrder,
                      newOrder: newReportsOrder,
                    }),
                    websiteSection: 'report',
                    functionName: 'updateSavedReportsOrder',
                    pagePath: window.location.pathname,
                  },
                })
              }}
            >
              <Droppable droppableId="reportSorter">
                {(droppableProvided) => {
                  return (
                    <tbody
                      ref={droppableProvided.innerRef}
                      className={styles.dragAndDropDraggableContainer}
                      {...droppableProvided.droppableProps}
                    >
                      {savedReports.map((report, index) => {
                        const {
                          savedReportID,
                          reportName,
                          availableTo,
                        } = report

                        return (
                          <Draggable
                            key={savedReportID}
                            draggableId={savedReportID}
                            index={index}
                          >
                            {(draggableProvided) => {
                              return (
                                <tr
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                >
                                  <td className={styles.handleColumn}>
                                    <BurgerIcon
                                      {...draggableProvided.dragHandleProps}
                                    />
                                  </td>
                                  <td className={styles.reportNameColumn}>
                                    {reportName}
                                  </td>
                                  <td className={styles.visibilityColumn}>
                                    {availableToShareOptions[availableTo].long}
                                    <b
                                      className={classNames(
                                        styles.availableToIcon,
                                        {
                                          [styles[availableTo]]: true,
                                        },
                                      )}
                                    />
                                  </td>
                                  <td className={styles.actionsColumn}>
                                    <Button
                                      variant="iconOnly"
                                      className={styles.editButton}
                                      icon={{ alt: 'Edit', src: EditIcon }}
                                      onPress={() => {
                                        setReportToEdit({
                                          ...report,
                                          granularity: report.granularity as Granularity,
                                          availableTo: report.availableTo as
                                            | 'company'
                                            | 'account'
                                            | 'user',
                                        })
                                        setShowSaveReportModal(true)
                                      }}
                                    />
                                    <DeleteButton
                                      onPress={() =>
                                        setConfirmDeleteId(savedReportID)
                                      }
                                    />
                                  </td>
                                </tr>
                              )
                            }}
                          </Draggable>
                        )
                      })}
                      {droppableProvided.placeholder}
                    </tbody>
                  )
                }}
              </Droppable>
            </DragDropContext>
          </Table>
        )}
      </Modal>
      {confirmDeleteId && (
        <Modal
          setIsOpen={() => setConfirmDeleteId(null)}
          modalHeader="Confirm report deletion"
          isWarning
          headerColor="pink"
          yesButtonLoading={deletingReport}
          yesText="Delete"
          onYes={async () => {
            await deleteSavedLostLinkReport({
              variables: {
                savedReportID: confirmDeleteId,
              },
            })

            logAction({
              variables: {
                action: 'saved-lost-link-reports-delete-report',
                extra: confirmDeleteId,
                websiteSection: 'report',
                functionName: 'deleteSavedReport',
                pagePath: window.location.pathname,
              },
            })

            setConfirmDeleteId(null)
          }}
        >
          <p style={{ margin: 0 }}>
            You will no longer be able to access this report.
          </p>
        </Modal>
      )}
    </>
  )
}

interface SaveEditReportModalProps {
  toggleActive: React.Dispatch<React.SetStateAction<boolean>>
  loading?: boolean
  onSuccess: (val: SavedReport, isNew?: boolean) => void
  currentReport: SavedReport | null
  /** Allows editing reports other than the current one */
  reportToEdit?: SavedReport | null
  setReportToEdit: React.Dispatch<React.SetStateAction<SavedReport | null>>
  getNewReportConfig: () => Partial<SavedReport>
}

export function SaveEditReportModal({
  toggleActive,
  loading,
  onSuccess,
  currentReport = null,
  reportToEdit,
  setReportToEdit,
  getNewReportConfig,
}: SaveEditReportModalProps): React.ReactElement {
  const [report, setReport] = useState<Partial<SavedReport> | null>(
    reportToEdit || currentReport,
  )

  return (
    <Modal
      setIsOpen={toggleActive}
      modalHeader={report ? 'Update existing report' : 'Create a new report'}
      yesText={reportToEdit ? 'Update' : 'Create'}
      yesButtonLoading={loading}
      onYes={() => {
        onSuccess(
          (report as SavedReport) || {
            ...getNewReportConfig(),
            reportName: messages.defaultLostLinksReportTitle,
            availableTo: 'user',
          },
          !reportToEdit,
        )

        setReportToEdit(null)
      }}
      beforeClose={() => setReportToEdit(null)}
    >
      <LabelSlot topAlign className={styles.fullWidth}>
        <Label modalHeading>
          <Tooltip
            id="report-name-tooltip"
            useIcon
            tooltipMessage="Name of report to display in tab."
          >
            Report name
          </Tooltip>
        </Label>
      </LabelSlot>
      <Input
        name="reportName"
        id="reportName"
        type="text"
        value={report?.reportName || messages.defaultLostLinksReportTitle}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
          const { value: val } = event.target

          setReport((curr) => {
            if (!curr) {
              return {
                ...getNewReportConfig(),
                reportName: val,
                reportTitle: val,
                availableTo: report?.availableTo || 'user',
              }
            }

            return {
              ...curr,
              reportName: val,
              reportTitle: curr.reportTitle ? curr.reportTitle : val,
              availableTo: report?.availableTo || 'user',
            }
          })
        }}
      />
      <LabelSlot topAlign className={styles.fullWidth}>
        <Label modalHeading>Who can see this</Label>
      </LabelSlot>
      <div className={styles.radioPanel}>
        <Input
          type="radio"
          id="availableTo-1"
          name="availableTo"
          className={classNames(
            styles.availableToInput,
            styles.availableToInputCompany,
          )}
          label={availableToShareOptions.company.long}
          checked={report?.availableTo === 'company'}
          onClick={() => {
            setReport((curr) => {
              if (!curr) {
                return {
                  ...getNewReportConfig(),
                  availableTo: 'company',
                }
              }

              return {
                ...curr,
                availableTo: 'company',
              }
            })
          }}
        >
          <p>{availableToShareOptions.company.desc}</p>
        </Input>
        <Input
          type="radio"
          id="availableTo-3"
          name="availableTo"
          className={classNames(
            styles.availableToInput,
            styles.availableToInputAccount,
          )}
          label={availableToShareOptions.account.long}
          checked={report?.availableTo === 'account'}
          onClick={() => {
            setReport((curr) => {
              if (!curr) {
                return {
                  ...getNewReportConfig(),
                  availableTo: 'account',
                }
              }

              return {
                ...curr,
                availableTo: 'account',
              }
            })
          }}
        >
          <p>{availableToShareOptions.account.desc}</p>
        </Input>
        <Input
          type="radio"
          id="availableTo-2"
          name="availableTo"
          className={classNames(styles.availableToInput, {
            [styles.availableToInputUser]: true,
          })}
          label={availableToShareOptions.user.long}
          checked={!report || report?.availableTo === 'user'}
          onClick={() => {
            setReport((curr) => {
              if (!curr) {
                return {
                  ...getNewReportConfig(),
                  availableTo: 'user',
                }
              }

              return {
                ...curr,
                availableTo: 'user',
              }
            })
          }}
        >
          <p>{availableToShareOptions.user.desc}</p>
        </Input>
      </div>
    </Modal>
  )
}

interface ManageLostLinkReportsTabsProps {
  getNewReportConfig: () => Partial<SavedReport>
  setShowSaveReportModal: React.Dispatch<React.SetStateAction<boolean>>
  showSaveButton?: boolean
  loading?: boolean
  loadingSavedReports?: boolean
  reportsOrder: string[]
  savedReports?: ListSavedLostLinksReportsQuery['report']['listSavedLinkAuditReports']['savedReports']
  /** Used to set current report automatically, based on URLs */
  currentReport: SavedReport | null
  onResetReport: (resetToReport: SavedReport | null) => void
  onSaveReport: () => void
  updateSavedReport: any
  children: React.ReactNode
}

export default function ManageLostLinkReportsTabs({
  getNewReportConfig,
  setShowSaveReportModal,
  showSaveButton = false,
  loading,
  loadingSavedReports,
  reportsOrder,
  savedReports = [],
  currentReport,
  onResetReport,
  onSaveReport,
  updateSavedReport,
  children,
}: ManageLostLinkReportsTabsProps) {
  const logAction = useLogAction()

  const [currentIndex, setCurrentIndex] = useState(0)

  const tabsLabels = useMemo(() => {
    const res: (string | React.ReactElement)[] = ['Unfiltered report']

    if (reportsOrder.length > 2) {
      reportsOrder.forEach((orderKey) => {
        const found = getItemByKeyValue(savedReports, 'savedReportID', orderKey)
        if (found !== -1) {
          const { reportName, availableTo } = found
          if (reportName) {
            res.push(
              `${reportName} ${availableTo === 'user' ? `(private)` : ''}`,
            )
          }
        }
      })
      return res
    }

    reportsOrder.forEach((orderKey) => {
      const found = getItemByKeyValue(savedReports, 'savedReportID', orderKey)
      if (found !== -1) {
        const { reportName, availableTo } = found
        if (reportName) {
          res.push(
            <em key={nanoid()} className={styles.tabText}>
              {reportName}{' '}
              <b
                className={classNames(styles.availableToIcon, {
                  [styles[availableTo]]: true,
                })}
              />
            </em>,
          )
        }
      }
    })

    return res
  }, [reportsOrder, savedReports])

  useEffect(() => {
    if (!currentReport) {
      setCurrentIndex(0)
      return
    }

    const foundReportIndex = Math.max(
      reportsOrder.findIndex(
        (savedReportId) => savedReportId === currentReport?.savedReportID,
      ),
      0,
    )

    setCurrentIndex(foundReportIndex + 1)
  }, [currentReport, reportsOrder])

  return (
    <>
      <HeaderPanel className={styles.headerPanel}>
        <Panel className={styles.panel}>
          <ButtonTabs
            className={styles.buttonTabs}
            showOrphansAsTab
            selected={currentIndex}
            isTopOfBox
            showDropDown
            tabsLabels={tabsLabels}
            type="tabs"
            onChange={(index: number) => {
              if (index === currentIndex) {
                onResetReport(currentReport)
              } else if (index === 0) {
                onResetReport(null)
              } else {
                const found = {
                  ...savedReports[index - 1],
                }
                onResetReport(found as SavedReport)
              }
            }}
          >
            {[
              ...tabsLabels.map((item) => {
                return (
                  <span key={nanoid()} className={styles.tabBoxPlaceholder}>
                    {item}
                  </span>
                )
              }),
              loadingSavedReports || loading ? (
                <div key={nanoid()} className={styles.afterTabsButtons} />
              ) : (
                <div
                  key="modal-edit-button"
                  className={styles.afterTabsButtons}
                >
                  {showSaveButton && (
                    <Button
                      variant="secondary"
                      color="blue"
                      style={{ padding: '2px 12px' }}
                      onPress={async () => {
                        if (currentReport) {
                          await updateSavedReport({
                            variables: {
                              ...currentReport,
                              ...getNewReportConfig(),
                            },
                          })

                          logAction({
                            variables: {
                              action: 'saved-lost-link-reports-update-existing',
                              extra: JSON.stringify({
                                ...currentReport,
                                ...getNewReportConfig(),
                              }),
                              websiteSection: 'report',
                              functionName: 'updateSavedReport',
                              pagePath: window.location.pathname,
                            },
                          })

                          onSaveReport()
                        } else {
                          setShowSaveReportModal(true)
                        }
                      }}
                    >
                      {currentReport ? 'Save changes' : 'Save new report'}
                    </Button>
                  )}
                </div>
              ),
              <div key={nanoid()} className={styles.afterTabsActionButtons}>
                {children}
              </div>,
            ]}
          </ButtonTabs>
        </Panel>
      </HeaderPanel>
    </>
  )
}
